import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { NgxPermissionsService } from 'ngx-permissions';

const _keyVer = environment.localStorageKeyVerPrefix || '_v3_';
const LS_TOKEN_KEY = _keyVer + '__temp_access_token';
const LS_EXPIRES_AT_KEY = _keyVer + '__temp_access_expires_at_ms';
// Clave PROPIA de la sesión temporal — NO usar 'userData_permission' (compartida con la sesión normal).
const LS_TEMP_PERMS_KEY = _keyVer + '__temp_access_permissions';
// Marca por pestaña (sessionStorage = per-tab). Una pestaña marcada nunca
// puede ejecutar requests con credenciales normales (OIDC / _v3___token),
// aunque el localStorage compartido tenga datos de la otra sesión.
const SS_TAB_MARK_KEY = _keyVer + '__is_temp_tab';

const TEMP_ACCESS_PERMISSIONS = [
  'spaces_view',
  'spaces_planograms_view',
  'spaces_planograms_messaging',
  'spaces_planograms_upload_audit_photo',
];

@Injectable({ providedIn: 'root' })
export class TempAccessSessionService {
  constructor(private permissionsService: NgxPermissionsService) {}

  setSession(accessToken: string, expiresInSeconds: number): void {
    const now = Date.now();
    const skewMs = 10_000; // margen para evitar expiración al borde
    const expiresAtMs = now + Math.max(0, Number(expiresInSeconds || 0) * 1000 - skewMs);

    localStorage.setItem(LS_TOKEN_KEY, accessToken);
    localStorage.setItem(LS_EXPIRES_AT_KEY, String(expiresAtMs));

    // Permisos mínimos para pasar guards de navegación (no es seguridad real).
    // Se guardan en una clave PROPIA para no pisar los de la sesión normal de otra pestaña.
    localStorage.setItem(LS_TEMP_PERMS_KEY, JSON.stringify(TEMP_ACCESS_PERMISSIONS));
    this.permissionsService.loadPermissions(TEMP_ACCESS_PERMISSIONS);

    this.markTabAsTemporary();
  }

  clearSession(): void {
    localStorage.removeItem(LS_TOKEN_KEY);
    localStorage.removeItem(LS_EXPIRES_AT_KEY);
    localStorage.removeItem(LS_TEMP_PERMS_KEY);
    this.clearTabMark();
  }

  /**
   * Rehidrata los permisos temporales (en memoria) en una recarga de la pestaña invitado,
   * leyendo la clave PROPIA y nunca 'userData_permission' de la sesión normal.
   */
  loadPersistedPermissions(): void {
    try {
      const raw = localStorage.getItem(LS_TEMP_PERMS_KEY);
      const perms = raw ? JSON.parse(raw) : null;
      this.permissionsService.loadPermissions(perms?.length ? perms : TEMP_ACCESS_PERMISSIONS);
    } catch {
      this.permissionsService.loadPermissions(TEMP_ACCESS_PERMISSIONS);
    }
  }

  markTabAsTemporary(): void {
    try {
      sessionStorage.setItem(SS_TAB_MARK_KEY, '1');
    } catch {}
  }

  clearTabMark(): void {
    try {
      sessionStorage.removeItem(SS_TAB_MARK_KEY);
    } catch {}
  }

  isTabTemporary(): boolean {
    try {
      return sessionStorage.getItem(SS_TAB_MARK_KEY) === '1';
    } catch {
      return false;
    }
  }

  isValid(): boolean {
    return this.getToken() != null;
  }

  getToken(): string | null {
    const token = localStorage.getItem(LS_TOKEN_KEY);
    if (!token) return null;

    const expiresAtMsRaw = localStorage.getItem(LS_EXPIRES_AT_KEY);
    const expiresAtMs = expiresAtMsRaw ? Number(expiresAtMsRaw) : 0;
    if (!expiresAtMs || Number.isNaN(expiresAtMs)) return null;

    if (Date.now() >= expiresAtMs) {
      this.clearSession();
      return null;
    }

    return token;
  }

  isAllowedGuestRoute(url: string): boolean {
    const normalizedUrl = this.normalizeUrlPath(url);
    if (!normalizedUrl) return false;

    return /^\/spaces\/planograms\/guest-list\/?$/.test(normalizedUrl) || /^\/spaces\/planograms\/edit\/\d+\/?$/.test(normalizedUrl);
  }

  isAllowedGuestLoadPath(path: string): boolean {
    return (path || '').trim().toLowerCase() === 'planograms';
  }

  isAllowedGuestRequest(url: string): boolean {
    const normalizedUrl = String(url || '').toLowerCase();
    if (!normalizedUrl) return false;

    const temporaryListRegex = /\/api\/spaces\/planograms\/temporary-access-list(?:\?|$)/;
    const planogramByIdRegex = /\/api\/spaces\/planograms\/\d+(?:\?|$)/;
    const temporaryAccessPlanogramByIdRegex = /\/api\/spaces\/planograms\/temporary-access-planogram\/\d+(?:\?|$)/;
    const temporaryAccessAuditByIdRegex = /\/api\/spaces\/planograms\/temporary-access-audit\/\d+(?:\?|$)/;
    const planogramAuditByIdRegex = /\/api\/spaces\/planograms\/get-audit\/\d+(?:\?|$)/;
    const planogramTemporaryAccessSelectRegex = /\/api\/spaces\/planograms\/temporary-access-select(?:\?|$)/;
    const planogramCommentsByIdRegex = /\/api\/spaces\/planograms\/get-planogram-comments\/\d+(?:\?|$)/;
    const planogramCommentsPostRegex = /\/api\/spaces\/planograms\/post-planogram-comment(?:\?|$)/;
    const planogramTemporaryAccessCommentRegex = /\/api\/spaces\/planograms\/temporary-access-comment(?:\?|$)/;
    const planogramTemporaryAccessGetCommentsRegex = /\/api\/spaces\/planograms\/temporary-access-comments(?:\/\d+)?(?:\?|$)/;
    const planogramTemporaryAccessUploadImgRegex = /\/api\/spaces\/planograms\/temporary-access-upload-img(?:\?|$)/;
    const planogramTemporaryAccessImplantRegex = /\/api\/spaces\/planograms\/temporary-access-implant(?:\?|$)/;
    const planogramUploadAuditImgRegex = /\/api\/spaces\/planograms\/upload-img-audit(?:\?|$)/;
    const itemVariantsTemporaryAccessEquivalenceGroupsRegex = /\/api\/item-variants\/temporary-access\/equivalence-groups(?:\?|$)/;
    const planogramTemporaryAccessEquivalenceReportRegex = /\/api\/spaces\/planograms\/temporary-access-equivalence-report\/\d+(?:\?|$)/;

    return (
      temporaryListRegex.test(normalizedUrl) ||
      planogramByIdRegex.test(normalizedUrl) ||
      temporaryAccessPlanogramByIdRegex.test(normalizedUrl) ||
      temporaryAccessAuditByIdRegex.test(normalizedUrl) ||
      planogramAuditByIdRegex.test(normalizedUrl) ||
      planogramTemporaryAccessSelectRegex.test(normalizedUrl) ||
      planogramCommentsByIdRegex.test(normalizedUrl) ||
      planogramCommentsPostRegex.test(normalizedUrl) ||
      planogramTemporaryAccessCommentRegex.test(normalizedUrl) ||
      planogramTemporaryAccessGetCommentsRegex.test(normalizedUrl) ||
      planogramTemporaryAccessUploadImgRegex.test(normalizedUrl) ||
      planogramTemporaryAccessImplantRegex.test(normalizedUrl) ||
      planogramUploadAuditImgRegex.test(normalizedUrl) ||
      itemVariantsTemporaryAccessEquivalenceGroupsRegex.test(normalizedUrl) ||
      planogramTemporaryAccessEquivalenceReportRegex.test(normalizedUrl)
    );
  }

  private normalizeUrlPath(url: string): string {
    const cleanUrl = String(url || '')
      .split('?')[0]
      .split('#')[0];
    return cleanUrl || '';
  }
}
